import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { format } from 'date-fns';
import type { Room } from 'livekit-client';
import { RoomEvent } from 'livekit-client';
import {
  AlertCircle,
  Download,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  LockKeyhole,
  Paperclip,
  RefreshCw,
  Send,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  createOnboardingInterviewChatMessage,
  deleteOnboardingInterviewChatAttachment,
  fetchOnboardingInterviewChatAttachment,
  listOnboardingInterviewChatMessages,
  refreshPublicOnboardingInterviewChatToken,
  uploadOnboardingInterviewChatAttachment,
  type OnboardingInterviewChatAccess,
  type OnboardingInterviewChatAttachment,
  type OnboardingInterviewChatAudience,
  type OnboardingInterviewChatMessage,
} from '../services/onboardingInterviewService';

const chatTopic = 'geoescala-meeting-chat';
const chatTypingTopic = 'geoescala-meeting-chat-typing';
const typingPublishIntervalMs = 1000;
const typingIdleTimeoutMs = 1800;
const remoteTypingTimeoutMs = 3000;
const maxMessageLength = 4000;
const maxAttachments = 5;
const acceptedFiles = '.jpg,.jpeg,.png,.webp,.pdf,.docx,.xlsx,.txt';
const messageUrlPattern = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const trailingUrlPunctuationPattern = /[.,!?;:]+$/;
const myMessageBubbleClassName =
  'rounded-md bg-sky-600 px-3 py-2 text-sm text-white';
const otherMessageBubbleClassName =
  'rounded-md border border-border/70 bg-card px-3 py-2 text-sm text-card-foreground shadow-sm dark:bg-muted dark:text-foreground';

type PendingAttachment = {
  key: string;
  file: File;
  progress: number;
  status: 'uploading' | 'ready' | 'error';
  attachment?: OnboardingInterviewChatAttachment;
  error?: string;
};

type TypingParticipant = {
  identity: string;
  name: string;
};

type CandidateRecipient = {
  interviewId: number;
  name: string;
};

const candidateInterviewIdFromIdentity = (identity: string) => {
  const match = /^candidate-(\d+)-/.exec(identity);
  const interviewId = Number(match?.[1]);
  return Number.isInteger(interviewId) && interviewId > 0
    ? interviewId
    : undefined;
};

type InterviewMeetingChatPanelProps = {
  access: OnboardingInterviewChatAccess;
  room?: Room;
  open: boolean;
  historyOnly?: boolean;
  currentParticipantIdentity?: string;
  onClose: () => void;
  onIncomingMessage?: (message: OnboardingInterviewChatMessage) => void;
  onUnreadChange?: (count: number) => void;
};

const errorMessage = (error: unknown, fallback: string) => {
  const candidate = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    candidate?.response?.data?.error ||
    candidate?.response?.data?.message ||
    candidate?.message ||
    fallback
  );
};

const mergeMessages = (
  current: OnboardingInterviewChatMessage[],
  incoming: OnboardingInterviewChatMessage[],
) => {
  const byId = new Map<number, OnboardingInterviewChatMessage>();
  current.forEach((message) => byId.set(message.id, message));
  incoming.forEach((message) => byId.set(message.id, message));
  return [...byId.values()].sort((left, right) => left.id - right.id);
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const MessageContent = ({ content }: { content: string }) => {
  const parts: ReactNode[] = [];
  let cursor = 0;

  for (const match of content.matchAll(messageUrlPattern)) {
    const start = match.index ?? 0;
    const matchedText = match[0];
    const urlText = matchedText.replace(trailingUrlPunctuationPattern, '');
    const trailingText = matchedText.slice(urlText.length);

    if (start > cursor) parts.push(content.slice(cursor, start));
    parts.push(
      <a
        key={`${start}-${urlText}`}
        href={urlText.startsWith('www.') ? `https://${urlText}` : urlText}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all font-medium text-sky-700 underline decoration-sky-500/60 underline-offset-2 hover:text-sky-800 dark:text-sky-300 dark:hover:text-sky-200"
      >
        {urlText}
      </a>,
    );
    if (trailingText) parts.push(trailingText);
    cursor = start + matchedText.length;
  }

  if (cursor < content.length) parts.push(content.slice(cursor));
  return <>{parts}</>;
};

const AttachmentPreview = ({
  attachment,
  access,
}: {
  attachment: OnboardingInterviewChatAttachment;
  access: OnboardingInterviewChatAccess;
}) => {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(attachment.is_image);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!attachment.is_image) return;
    let active = true;
    let objectUrl = '';
    fetchOnboardingInterviewChatAttachment(access, attachment.id)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      })
      .catch(() => active && setFailed(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    access.accessToken,
    access.context,
    access.interviewId,
    attachment.id,
    attachment.is_image,
  ]);

  const download = async () => {
    try {
      setLoading(true);
      const blob = await fetchOnboardingInterviewChatAttachment(
        access,
        attachment.id,
      );
      triggerBlobDownload(blob, attachment.file_name);
    } finally {
      setLoading(false);
    }
  };

  if (attachment.is_image && imageUrl) {
    return (
      <button
        type="button"
        className="group relative block max-w-full overflow-hidden rounded-md border border-border/70 bg-muted"
        onClick={() => void download()}
        title={t('onboarding_interviews.chat.download')}
      >
        <img
          src={imageUrl}
          alt={attachment.file_name}
          className="max-h-52 w-auto max-w-full object-contain"
        />
        <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100">
          <Download className="h-4 w-4" />
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="flex min-h-12 w-full items-center gap-2 rounded-md border border-border/70 bg-background/80 px-3 py-2 text-left text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
      onClick={() => void download()}
      disabled={loading || failed}
      title={t('onboarding_interviews.chat.download')}
    >
      {loading ? (
        <LoaderCircle className="h-4 w-4 shrink-0 animate-spin" />
      ) : failed ? (
        <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
      ) : attachment.is_image ? (
        <ImageIcon className="h-4 w-4 shrink-0" />
      ) : (
        <FileText className="h-4 w-4 shrink-0" />
      )}
      <span className="min-w-0 flex-1 truncate text-xs font-medium">
        {attachment.file_name}
      </span>
      {!loading && !failed ? <Download className="h-4 w-4 shrink-0" /> : null}
    </button>
  );
};

export default function InterviewMeetingChatPanel({
  access: initialAccess,
  room,
  open,
  historyOnly = false,
  currentParticipantIdentity,
  onClose,
  onIncomingMessage,
  onUnreadChange,
}: InterviewMeetingChatPanelProps) {
  const { t, i18n } = useTranslation();
  const [accessToken, setAccessToken] = useState(initialAccess.accessToken);
  const access = useMemo(
    () => ({ ...initialAccess, accessToken }),
    [
      initialAccess.context,
      initialAccess.interviewId,
      initialAccess.isPublic,
      accessToken,
    ],
  );
  const [messages, setMessages] = useState<OnboardingInterviewChatMessage[]>(
    [],
  );
  const [content, setContent] = useState('');
  const [audience, setAudience] =
    useState<OnboardingInterviewChatAudience>('room');
  const [recipientInterviewId, setRecipientInterviewId] = useState<number>();
  const [candidateRecipients, setCandidateRecipients] = useState<
    CandidateRecipient[]
  >([]);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [unread, setUnread] = useState(0);
  const [typingParticipants, setTypingParticipants] = useState<
    TypingParticipant[]
  >([]);
  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);
  const knownMessageIdsRef = useRef(new Set<number>());
  const openRef = useRef(open);
  const currentParticipantIdentityRef = useRef(currentParticipantIdentity);
  const onIncomingMessageRef = useRef(onIncomingMessage);
  const typingPublishedRef = useRef(false);
  const lastTypingPublishedAtRef = useRef(0);
  const typingStopTimeoutRef = useRef<number | null>(null);
  const remoteTypingTimeoutsRef = useRef(new Map<string, number>());

  openRef.current = open;
  currentParticipantIdentityRef.current = currentParticipantIdentity;
  onIncomingMessageRef.current = onIncomingMessage;

  const notifyIncomingMessage = useCallback(
    (message: OnboardingInterviewChatMessage) => {
      if (
        openRef.current ||
        message.sender_identity === currentParticipantIdentityRef.current
      ) {
        return;
      }
      setUnread((count) => count + 1);
      onIncomingMessageRef.current?.(message);
    },
    [],
  );

  const publishTypingState = useCallback(
    (typing: boolean) => {
      if (!room || historyOnly) return;
      if (typing && audience !== 'room') return;
      const now = Date.now();
      if (
        typing &&
        typingPublishedRef.current &&
        now - lastTypingPublishedAtRef.current < typingPublishIntervalMs
      ) {
        return;
      }
      if (!typing && !typingPublishedRef.current) return;

      typingPublishedRef.current = typing;
      lastTypingPublishedAtRef.current = typing ? now : 0;
      void room.localParticipant
        .publishData(
          new TextEncoder().encode(
            JSON.stringify({ type: 'chat_typing', typing }),
          ),
          { reliable: false, topic: chatTypingTopic },
        )
        .catch(() => undefined);
    },
    [audience, historyOnly, room],
  );

  const stopTyping = useCallback(() => {
    if (typingStopTimeoutRef.current) {
      window.clearTimeout(typingStopTimeoutRef.current);
      typingStopTimeoutRef.current = null;
    }
    publishTypingState(false);
  }, [publishTypingState]);

  const markTyping = useCallback(() => {
    publishTypingState(true);
    if (typingStopTimeoutRef.current) {
      window.clearTimeout(typingStopTimeoutRef.current);
    }
    typingStopTimeoutRef.current = window.setTimeout(
      stopTyping,
      typingIdleTimeoutMs,
    );
  }, [publishTypingState, stopTyping]);

  const loadMessages = useCallback(
    async (beforeId?: number, silent = false) => {
      if (!silent) beforeId ? setLoadingOlder(true) : setLoading(true);
      try {
        const items = await listOnboardingInterviewChatMessages(
          access,
          beforeId,
        );
        if (initializedRef.current && !beforeId) {
          items.forEach((message) => {
            if (!knownMessageIdsRef.current.has(message.id)) {
              notifyIncomingMessage(message);
            }
          });
        }
        items.forEach((message) => knownMessageIdsRef.current.add(message.id));
        setMessages((current) =>
          beforeId
            ? mergeMessages(items, current)
            : mergeMessages(current, items),
        );
        setHasOlder(items.length === 50);
        setError('');
      } catch (loadError) {
        if (!silent) {
          setError(
            errorMessage(loadError, t('onboarding_interviews.chat.load_error')),
          );
        }
      } finally {
        setLoading(false);
        setLoadingOlder(false);
        initializedRef.current = true;
      }
    },
    [access, notifyIncomingMessage, t],
  );

  useEffect(() => {
    setAccessToken(initialAccess.accessToken);
  }, [initialAccess.accessToken]);

  useEffect(() => {
    if (!room || initialAccess.isPublic) {
      setCandidateRecipients([]);
      return;
    }
    const syncCandidates = () => {
      const byInterview = new Map<number, CandidateRecipient>();
      room.remoteParticipants.forEach((participant) => {
        const interviewId = candidateInterviewIdFromIdentity(
          participant.identity,
        );
        if (!interviewId) return;
        const current = byInterview.get(interviewId);
        const name = participant.name || current?.name || 'Candidato';
        byInterview.set(interviewId, { interviewId, name });
      });
      setCandidateRecipients(
        [...byInterview.values()].sort((left, right) =>
          left.name.localeCompare(right.name),
        ),
      );
    };
    syncCandidates();
    room.on(RoomEvent.ParticipantConnected, syncCandidates);
    room.on(RoomEvent.ParticipantDisconnected, syncCandidates);
    room.on(RoomEvent.ParticipantNameChanged, syncCandidates);
    return () => {
      room.off(RoomEvent.ParticipantConnected, syncCandidates);
      room.off(RoomEvent.ParticipantDisconnected, syncCandidates);
      room.off(RoomEvent.ParticipantNameChanged, syncCandidates);
    };
  }, [initialAccess.isPublic, room]);

  useEffect(() => {
    if (
      audience === 'candidate' &&
      !candidateRecipients.some(
        (candidate) => candidate.interviewId === recipientInterviewId,
      )
    ) {
      setAudience('room');
      setRecipientInterviewId(undefined);
    }
  }, [audience, candidateRecipients, recipientInterviewId]);

  useEffect(() => {
    void loadMessages();
    const interval = window.setInterval(
      () => void loadMessages(undefined, true),
      15_000,
    );
    return () => window.clearInterval(interval);
  }, [loadMessages]);

  useEffect(() => {
    if (!initialAccess.isPublic || !accessToken) return;
    const expiresAt = new Date(
      initialAccess.accessToken === accessToken
        ? initialAccess.accessTokenExpiresAt || ''
        : Date.now() + 55 * 60_000,
    ).getTime();
    const delay = Math.max(30_000, expiresAt - Date.now() - 5 * 60_000);
    const timeout = window.setTimeout(() => {
      refreshPublicOnboardingInterviewChatToken(accessToken)
        .then((token) => setAccessToken(token.chat_access_token))
        .catch(() => setError(t('onboarding_interviews.chat.session_expired')));
    }, delay);
    return () => window.clearTimeout(timeout);
  }, [
    accessToken,
    initialAccess.accessToken,
    initialAccess.accessTokenExpiresAt,
    initialAccess.isPublic,
    t,
  ]);

  useEffect(() => {
    if (!room) return;
    const receive = (
      payload: Uint8Array,
      participant: unknown,
      _kind: unknown,
      topic?: string,
    ) => {
      if (topic !== chatTopic || participant) return;
      try {
        const event = JSON.parse(new TextDecoder().decode(payload)) as {
          type?: string;
          message?: OnboardingInterviewChatMessage;
        };
        if (event.type !== 'chat_message_created' || !event.message?.id) return;
        const isNewMessage = !knownMessageIdsRef.current.has(event.message.id);
        knownMessageIdsRef.current.add(event.message.id);
        setMessages((current) => mergeMessages(current, [event.message!]));
        if (isNewMessage) notifyIncomingMessage(event.message);
      } catch {
        // Ignore malformed or unrelated room data.
      }
    };
    room.on(RoomEvent.DataReceived, receive);
    return () => {
      room.off(RoomEvent.DataReceived, receive);
    };
  }, [notifyIncomingMessage, room]);

  useEffect(() => {
    if (!room) return;
    const receiveTyping = (
      payload: Uint8Array,
      participant?: {
        identity?: string;
        name?: string;
        isLocal?: boolean;
      },
      _kind?: unknown,
      topic?: string,
    ) => {
      if (
        topic !== chatTypingTopic ||
        participant?.isLocal ||
        !participant?.identity
      ) {
        return;
      }
      try {
        const event = JSON.parse(new TextDecoder().decode(payload)) as {
          type?: string;
          typing?: boolean;
        };
        if (event.type !== 'chat_typing' || typeof event.typing !== 'boolean') {
          return;
        }

        const identity = participant.identity;
        const previousTimeout = remoteTypingTimeoutsRef.current.get(identity);
        if (previousTimeout) window.clearTimeout(previousTimeout);

        if (!event.typing) {
          remoteTypingTimeoutsRef.current.delete(identity);
          setTypingParticipants((current) =>
            current.filter((item) => item.identity !== identity),
          );
          return;
        }

        setTypingParticipants((current) => [
          ...current.filter((item) => item.identity !== identity),
          { identity, name: participant.name || identity },
        ]);
        const timeout = window.setTimeout(() => {
          remoteTypingTimeoutsRef.current.delete(identity);
          setTypingParticipants((current) =>
            current.filter((item) => item.identity !== identity),
          );
        }, remoteTypingTimeoutMs);
        remoteTypingTimeoutsRef.current.set(identity, timeout);
      } catch {
        // Ignore malformed typing packets from the room.
      }
    };
    room.on(RoomEvent.DataReceived, receiveTyping);
    return () => {
      room.off(RoomEvent.DataReceived, receiveTyping);
    };
  }, [room]);

  useEffect(() => {
    if (!open) stopTyping();
  }, [open, stopTyping]);

  useEffect(
    () => () => {
      if (typingStopTimeoutRef.current) {
        window.clearTimeout(typingStopTimeoutRef.current);
      }
      remoteTypingTimeoutsRef.current.forEach(window.clearTimeout);
      if (typingPublishedRef.current) publishTypingState(false);
    },
    [publishTypingState],
  );

  useEffect(() => {
    if (!open) return;
    setUnread(0);
    window.setTimeout(() => {
      if (listRef.current)
        listRef.current.scrollTop = listRef.current.scrollHeight;
    }, 40);
  }, [messages.length, open]);

  useEffect(() => onUnreadChange?.(unread), [onUnreadChange, unread]);

  const upload = async (entry: PendingAttachment) => {
    setPending((items) =>
      items.map((item) =>
        item.key === entry.key
          ? { ...item, status: 'uploading', progress: 0, error: undefined }
          : item,
      ),
    );
    try {
      const attachment = await uploadOnboardingInterviewChatAttachment(
        access,
        entry.file,
        (progress) =>
          setPending((items) =>
            items.map((item) =>
              item.key === entry.key ? { ...item, progress } : item,
            ),
          ),
      );
      setPending((items) =>
        items.map((item) =>
          item.key === entry.key
            ? { ...item, attachment, status: 'ready', progress: 100 }
            : item,
        ),
      );
    } catch (uploadError) {
      setPending((items) =>
        items.map((item) =>
          item.key === entry.key
            ? {
                ...item,
                status: 'error',
                error: errorMessage(
                  uploadError,
                  t('onboarding_interviews.chat.upload_error'),
                ),
              }
            : item,
        ),
      );
    }
  };

  const selectFiles = (files: FileList | null) => {
    if (!files) return;
    const capacity = Math.max(0, maxAttachments - pending.length);
    const entries = [...files].slice(0, capacity).map((file) => ({
      key: `${file.name}-${file.size}-${crypto.randomUUID()}`,
      file,
      progress: 0,
      status: 'uploading' as const,
    }));
    setPending((items) => [...items, ...entries]);
    entries.forEach((entry) => void upload(entry));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePending = async (entry: PendingAttachment) => {
    setPending((items) => items.filter((item) => item.key !== entry.key));
    if (entry.attachment) {
      await deleteOnboardingInterviewChatAttachment(
        access,
        entry.attachment.id,
      ).catch(() => undefined);
    }
  };

  const send = async () => {
    const attachmentIds = pending
      .map((entry) => entry.attachment?.id)
      .filter((id): id is number => Boolean(id));
    if (
      (!content.trim() && attachmentIds.length === 0) ||
      sending ||
      (audience === 'candidate' && !recipientInterviewId)
    ) {
      return;
    }
    stopTyping();
    setSending(true);
    setError('');
    try {
      const message = await createOnboardingInterviewChatMessage(
        access,
        content,
        attachmentIds,
        crypto.randomUUID(),
        audience,
        recipientInterviewId,
      );
      setMessages((current) => mergeMessages(current, [message]));
      setContent('');
      setPending([]);
    } catch (sendError) {
      setError(
        errorMessage(sendError, t('onboarding_interviews.chat.send_error')),
      );
    } finally {
      setSending(false);
    }
  };

  const groups = useMemo(() => {
    const result: OnboardingInterviewChatMessage[][] = [];
    messages.forEach((message) => {
      const previous = result[result.length - 1];
      const previousMessage = previous?.[previous.length - 1];
      const closeInTime =
        previousMessage &&
        Math.abs(
          new Date(message.created_at).getTime() -
            new Date(previousMessage.created_at).getTime(),
        ) <
          5 * 60_000;
      if (
        previous &&
        previousMessage.sender_identity === message.sender_identity &&
        previousMessage.audience === message.audience &&
        closeInTime
      ) {
        previous.push(message);
      } else {
        result.push([message]);
      }
    });
    return result;
  }, [messages]);

  const typingNames = useMemo(() => {
    const names = typingParticipants.map((participant) => participant.name);
    if (names.length < 2) return names[0] || '';
    const language = i18n.resolvedLanguage || i18n.language;
    const conjunction = language.startsWith('en')
      ? ' and '
      : language.startsWith('es')
        ? ' y '
        : ' e ';
    return `${names.slice(0, -1).join(', ')}${conjunction}${names[names.length - 1]}`;
  }, [i18n.language, i18n.resolvedLanguage, typingParticipants]);

  return (
    <section className="flex h-full min-h-0 flex-col bg-background text-foreground">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold">
            {t('onboarding_interviews.chat.title')}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t('onboarding_interviews.chat.subtitle')}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={onClose}
          aria-label={t('onboarding_interviews.chat.close')}
          title={t('onboarding_interviews.chat.close')}
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {hasOlder ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mb-3 w-full text-xs"
            disabled={loadingOlder}
            onClick={() => void loadMessages(messages[0]?.id)}
          >
            {loadingOlder ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {t('onboarding_interviews.chat.load_older')}
          </Button>
        ) : null}
        {loading ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <LoaderCircle className="h-5 w-5 animate-spin" />
          </div>
        ) : groups.length === 0 ? (
          <div className="flex h-full items-center justify-center px-8 text-center text-sm text-muted-foreground">
            {t('onboarding_interviews.chat.empty')}
          </div>
        ) : (
          <div className="grid gap-4">
            {groups.map((group) => {
              const first = group[0];
              const mine = first.sender_identity === currentParticipantIdentity;
              return (
                <div key={first.id} className={mine ? 'ml-8' : 'mr-8'}>
                  <div className="mb-1 flex items-center justify-between gap-2 px-1">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-xs font-semibold">
                        {mine
                          ? t('onboarding_interviews.chat.you')
                          : first.sender_name}
                      </span>
                      {first.audience !== 'room' ? (
                        <span className="flex shrink-0 items-center gap-1 text-[10px] font-medium text-muted-foreground">
                          <LockKeyhole className="h-3 w-3" />
                          {first.audience === 'interviewer'
                            ? t(
                                'onboarding_interviews.chat.audience_interviewer_badge',
                              )
                            : initialAccess.isPublic
                              ? t(
                                  'onboarding_interviews.chat.audience_candidate_you_badge',
                                )
                              : t(
                                  'onboarding_interviews.chat.audience_candidate_badge',
                                  {
                                    name:
                                      first.recipient_name ||
                                      t(
                                        'onboarding_interviews.chat.audience_candidate_fallback',
                                      ),
                                  },
                                )}
                        </span>
                      ) : null}
                    </div>
                    <time className="shrink-0 text-[11px] text-muted-foreground">
                      {format(new Date(first.created_at), 'HH:mm')}
                    </time>
                  </div>
                  <div className="grid gap-1.5">
                    {group.map((message) => (
                      <div
                        key={message.id}
                        className={
                          mine
                            ? myMessageBubbleClassName
                            : otherMessageBubbleClassName
                        }
                      >
                        {message.content ? (
                          <p className="whitespace-pre-wrap break-words leading-relaxed">
                            <MessageContent content={message.content} />
                          </p>
                        ) : null}
                        {message.attachments?.length ? (
                          <div
                            className={`${message.content ? 'mt-2' : ''} grid gap-2`}
                          >
                            {message.attachments.map((attachment) => (
                              <AttachmentPreview
                                key={attachment.id}
                                attachment={attachment}
                                access={access}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {error ? (
        <div className="mx-3 mb-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 break-words">{error}</span>
          <button type="button" onClick={() => setError('')}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : null}

      {!historyOnly ? (
        <footer className="shrink-0 border-t border-border p-3">
          <div className="mb-2 flex items-center gap-2">
            <span className="shrink-0 text-xs font-medium text-muted-foreground">
              {t('onboarding_interviews.chat.audience_label')}
            </span>
            {initialAccess.isPublic ? (
              <div
                className="grid min-w-0 flex-1 grid-cols-2 rounded-md border border-border bg-muted/50 p-0.5"
                role="group"
                aria-label={t('onboarding_interviews.chat.audience_label')}
              >
                <button
                  type="button"
                  className={`flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-sm px-2 text-xs font-medium transition-colors ${
                    audience === 'room'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                  aria-pressed={audience === 'room'}
                  onClick={() => {
                    stopTyping();
                    setAudience('room');
                    setRecipientInterviewId(undefined);
                  }}
                >
                  <Users className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">
                    {t('onboarding_interviews.chat.audience_room')}
                  </span>
                </button>
                <button
                  type="button"
                  className={`flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-sm px-2 text-xs font-medium transition-colors ${
                    audience === 'interviewer'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                  aria-pressed={audience === 'interviewer'}
                  onClick={() => {
                    stopTyping();
                    setAudience('interviewer');
                    setRecipientInterviewId(undefined);
                  }}
                >
                  <UserRound className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">
                    {t('onboarding_interviews.chat.audience_interviewer')}
                  </span>
                </button>
              </div>
            ) : (
              <Select
                value={
                  audience === 'candidate' && recipientInterviewId
                    ? `candidate:${recipientInterviewId}`
                    : 'room'
                }
                onValueChange={(value) => {
                  stopTyping();
                  if (value === 'room') {
                    setAudience('room');
                    setRecipientInterviewId(undefined);
                    return;
                  }
                  const interviewId = Number(value.replace('candidate:', ''));
                  setAudience('candidate');
                  setRecipientInterviewId(interviewId);
                }}
              >
                <SelectTrigger className="h-9 min-w-0 flex-1 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[80]">
                  <SelectItem value="room">
                    {t('onboarding_interviews.chat.audience_room')}
                  </SelectItem>
                  {candidateRecipients.map((candidate) => (
                    <SelectItem
                      key={candidate.interviewId}
                      value={`candidate:${candidate.interviewId}`}
                    >
                      {candidate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div
            className="flex h-6 items-center gap-2 overflow-hidden px-1 text-xs text-muted-foreground"
            aria-live="polite"
          >
            {typingParticipants.length ? (
              <>
                <span
                  className="flex shrink-0 items-center gap-0.5"
                  aria-hidden="true"
                >
                  <span className="h-1 w-1 animate-pulse rounded-full bg-current" />
                  <span className="h-1 w-1 animate-pulse rounded-full bg-current [animation-delay:150ms]" />
                  <span className="h-1 w-1 animate-pulse rounded-full bg-current [animation-delay:300ms]" />
                </span>
                <span className="truncate">
                  {t('onboarding_interviews.chat.typing', {
                    count: typingParticipants.length,
                    names: typingNames,
                  })}
                </span>
              </>
            ) : null}
          </div>
          {pending.length ? (
            <div className="mb-2 grid gap-1.5">
              {pending.map((entry) => (
                <div
                  key={entry.key}
                  className="flex min-h-11 items-center gap-2 rounded-md border border-border bg-muted/50 px-2 py-1.5"
                >
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">
                      {entry.file.name}
                    </p>
                    {entry.status === 'uploading' ? (
                      <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full bg-sky-600 transition-[width] duration-150"
                          style={{ width: `${entry.progress}%` }}
                        />
                      </div>
                    ) : entry.status === 'error' ? (
                      <p className="truncate text-[11px] text-destructive">
                        {entry.error}
                      </p>
                    ) : (
                      <p className="text-[11px] text-emerald-600">
                        {t('onboarding_interviews.chat.upload_ready')}
                      </p>
                    )}
                  </div>
                  {entry.status === 'error' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => void upload(entry)}
                      title={t('onboarding_interviews.chat.retry')}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => void removePending(entry)}
                    title={t('onboarding_interviews.chat.remove_attachment')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
            <Textarea
              value={content}
              maxLength={maxMessageLength}
              rows={2}
              className="min-h-16 resize-none border-0 bg-transparent px-3 py-2 shadow-none focus-visible:ring-0"
              placeholder={t('onboarding_interviews.chat.placeholder')}
              onChange={(event) => {
                setContent(event.target.value);
                markTyping();
              }}
              onBlur={stopTyping}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
            />
            <div className="flex items-center justify-between gap-2 px-2 pb-2">
              <div className="flex items-center gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  accept={acceptedFiles}
                  onChange={(event) => selectFiles(event.target.files)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={pending.length >= maxAttachments}
                  onClick={() => fileInputRef.current?.click()}
                  title={t('onboarding_interviews.chat.attach')}
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
                <span className="text-[11px] text-muted-foreground">
                  {content.length}/{maxMessageLength}
                </span>
              </div>
              <Button
                type="button"
                size="icon"
                className="h-9 w-9 bg-sky-600 text-white hover:bg-sky-700"
                disabled={
                  sending ||
                  (audience === 'candidate' && !recipientInterviewId) ||
                  pending.some((entry) => entry.status === 'uploading') ||
                  (!content.trim() &&
                    !pending.some((entry) => entry.attachment))
                }
                onClick={() => void send()}
                title={t('onboarding_interviews.chat.send')}
              >
                {sending ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </footer>
      ) : null}
    </section>
  );
}
